import React, { memo, useRef  } from 'react';
import { ActiveLoad, CompletedLoad } from './singleLoadCard';
import { LoadObject, calculateOffset } from './loadObject';
import { ExtraStopsAndTasksObject } from './extraStopsAndTasksObject';
import styles from './loadsCard.module.scss';
import { useBoardStore } from '../../boardStore';



export const LoadsCard = memo(({truckData, trucksSetDataMethodsObject, timelineStartInMills, currentTimeRef, overflowContainer, popupViewManagingObject})=>{
    const truckBoxRef = useRef(null);
    // Narrow selector: only subscribe to this specific truck, not the entire map
    const truck = useBoardStore(s => (truckData?.id != null ? s.trucksById?.[String(truckData.id)] : null)) || truckData;
    
    // Collect all load IDs this truck references
    const loadIds = React.useMemo(() => {
        const ids = [];
        if (truck.currentLoadId) ids.push(truck.currentLoadId);
        if (Array.isArray(truck.previousLoadIdsForPlannerView)) ids.push(...truck.previousLoadIdsForPlannerView);
        if (Array.isArray(truck.upcomingLoadIdsForPlannerView)) ids.push(...truck.upcomingLoadIdsForPlannerView);
        return ids;
    }, [truck.currentLoadId, truck.previousLoadIdsForPlannerView, truck.upcomingLoadIdsForPlannerView]);
    
    // Subscribe to all loadsById, then filter in useMemo to get stable reference
    const allLoadsById = useBoardStore(s => s.loadsById);
    
    // Only keep the loads this truck needs; current load id is applied last so it is never
    // replaced by the same id appearing in previous/upcoming lists.
    const loadsById = React.useMemo(() => {
        const result = {};
        const currentId = truck.currentLoadId != null ? Number(truck.currentLoadId) : null;
        for (const id of loadIds) {
            const numericId = Number(id);
            if (currentId != null && numericId === currentId) continue;
            if (allLoadsById?.[id]) result[id] = allLoadsById[id];
        }
        if (currentId != null && allLoadsById?.[currentId]) {
            result[currentId] = allLoadsById[currentId];
        }
        return result;
    }, [allLoadsById, loadIds, truck.currentLoadId]);

    // Build lists from normalized IDs (so load updates don't touch truck entity)
    // Memoize to prevent rebuilding on every render
    const { loads, lastSpecifiedOffset, lastSpecifiedOffsetForCompletedTasks } = React.useMemo(() => {
        const completed = (truck.previousLoadIdsForPlannerView || [])
            .map((id) => loadsById?.[id])
            .filter(Boolean)
            .map((l) => ({ ...l }));
        const current = truck.currentLoadId ? (loadsById?.[truck.currentLoadId] ? { ...loadsById[truck.currentLoadId] } : null) : null;
        const upcoming = (truck.upcomingLoadIdsForPlannerView || [])
            .map((id) => loadsById?.[id])
            .filter(Boolean)
            .map((l) => ({ ...l }));
        
        const loadObjects = [];
        let lastSpecifiedActionTime = null;

        if(completed){
            for(let i = 0; i < completed.length; i++){
                try {
                    // applyStartEndPercentages(completed[i], truck.id);
                    if(completed[i]["loadStatus"] === "Cancelled")continue;

                    const loadObject = new LoadObject(completed[i], timelineStartInMills, lastSpecifiedActionTime, currentTimeRef, false, false, truck.id);
                    lastSpecifiedActionTime = loadObject.loadsLastActionSpecified || null;
                    
                    loadObjects.push(loadObject);
                } catch (error) {
                }
            }
        }
        const computedLastSpecifiedOffsetForCompletedTasks = calculateOffset(lastSpecifiedActionTime || timelineStartInMills, timelineStartInMills);;
        if(current){
            
            if(current["loadStatus"] !== "Cancelled"){
                const loadObject = new LoadObject(current, timelineStartInMills, lastSpecifiedActionTime, currentTimeRef, true, false, truck.id);
                lastSpecifiedActionTime = loadObject.loadsLastActionSpecified || null;
                if(upcoming && upcoming.length > 0)loadObject.nextLoadId=upcoming[0].id;
                loadObjects.push(loadObject);
            }
            
        }
        if(upcoming){
            for(let i = 0; i < upcoming.length; i++){
                try {
                    if(current?.id !== upcoming[i]?.id && upcoming[i]["loadStatus"] !== "Cancelled"){
                        const loadObject = new LoadObject(upcoming[i], timelineStartInMills, lastSpecifiedActionTime, currentTimeRef, true, true, truck.id);
                        lastSpecifiedActionTime = loadObject.loadsLastActionSpecified || null;  
                        if(i < upcoming.length-1){
                            loadObject.nextLoadId=upcoming[i+1].id;
                        }                              
                        loadObjects.push(loadObject);
                    }
                    
                } catch (error) {
                }
            } 
        }
        
        const computedLastSpecifiedOffset = calculateOffset(lastSpecifiedActionTime || timelineStartInMills + 60000, timelineStartInMills);
        return { completedLoadData: completed, currentLoadData: current, upcomingLoadsData: upcoming, loads: loadObjects, lastSpecifiedOffset: computedLastSpecifiedOffset, lastSpecifiedOffsetForCompletedTasks : computedLastSpecifiedOffsetForCompletedTasks };
        // eslint-disable-next-line
    }, [truck.previousLoadIdsForPlannerView, truck.currentLoadId, truck.upcomingLoadIdsForPlannerView, truck.id, loadsById, timelineStartInMills, currentTimeRef]);    
    
    return <div ref={truckBoxRef} className={styles.plannerViewTruckCard} data-truckid={truck.id} data-hglht={truck.id != null ? String(truck.id) : ''} >
        {loads.map((load) =>{
            if(load.currentLoad){
                return <ActiveLoad key={load.loadId + `${load.upcomingLoad ? "U":"C"}`} overflowContainer={overflowContainer} popupViewManagingObject={popupViewManagingObject} loadData={load} timelineStartInMills={timelineStartInMills} currentTimeRef={currentTimeRef} truckId={truck.id} />
            }
            return <CompletedLoad key={load.loadId} loadData={load} />
        })}
        <ExtraStopsAndTasksObject truckBoxRef={truckBoxRef} truckId={truck.id} truckNumber={truck.truckNumber} trucksSetDataMethodsObject={trucksSetDataMethodsObject} timelineStartInMills={timelineStartInMills} currentTimeRef={currentTimeRef} overflowContainer={overflowContainer} popupViewManagingObject={popupViewManagingObject} lastSpecifiedOffset={lastSpecifiedOffset} lastSpecifiedOffsetForCompletedTasks={lastSpecifiedOffsetForCompletedTasks} loads={loads} taskCount={truck.tasks?.length}  />
        
    </div> 
})


// export function updateTruckRateDistribution(load, truckId, trucksSetDataMethodsObject, ignoreCurrentLoad, returnCurrentLoad){
//     const result = {}
//     const executorsArray = load.loadExecutors;
//     let lastPercentage = null;
//     for(let i = 0; i < executorsArray.length; i++){
//         const executor = JSON.parse(executorsArray[i]);
//         const currentTruck = executor.id === truckId;
//         const assignTo = currentTruck ? load : {};
//         if(i === 0){
//             assignTo.endPercentage = executor.percentage;
//         }else if(i === executorsArray.length-1){
//             assignTo.startPercentage = Number((1 - executor.percentage).toFixed(2));
//         }else{
//             executor.percentage = executor.percentage !== 0.0 ? Number((executor.percentage + lastPercentage).toFixed(2)) : 0.0;
//             assignTo.startPercentage = lastPercentage;
//             assignTo.endPercentage = executor.percentage;            
//         }
//         lastPercentage = executor.percentage;
//         if(!currentTruck){
//             if(executor.percentage === 0.0){
//                 delete assignTo.startPercentage;
//                 delete assignTo.endPercentage;
//                 assignTo.remove = true;
//             }
//             if(assignTo.startPercentage === 0){
//                 delete assignTo.startPercentage;
//             }
//             result[executor.truckNumber] = assignTo
//         }
//     }
//     let returnLoad = null;
//     if(returnCurrentLoad){
//         returnLoad = {...load}
//     }
//     const loadId = load.id;
//     if(!ignoreCurrentLoad){
//         const listenerName = typeof trucksSetDataMethodsObject["CL"+loadId] === "function" ? "CL"+loadId : typeof trucksSetDataMethodsObject["CLTD"+loadId] === "function" ? "CLTD"+loadId : null;
        
//         if(listenerName){ 
//             trucksSetDataMethodsObject[listenerName](load, "currentLoad", true);
//         }
//     }
//     delete load.startPercentage;
//     delete load.endPercentage;
//     setTimeout(() => {
//         for(const truckNumber of Object.keys(result)){
//             let loadCopy = {...load};
//             const paramSet = result[truckNumber];
//             const keySet = Object.keys(paramSet);
//             if(keySet[0] === "remove"){
//                 loadCopy = {removeLoad : true}
//             }else{
//                 for(const parameter of keySet){
//                     loadCopy[parameter] = paramSet[parameter];
//                 }
//             }
//             if(typeof trucksSetDataMethodsObject["CompletedLoadsFor"+truckNumber] === 'function'){
//                 trucksSetDataMethodsObject["CompletedLoadsFor"+truckNumber](loadId, loadCopy, true)
//             }
//         }
//     }, returnCurrentLoad ? 50 : 0);
//     if(returnLoad){
//         return returnLoad;
//     }
// }
// function completedLoadRateDistributionCheck(newData, oldData, truckId, trucksSetDataMethodsObject){
//     if(JSON.stringify(newData.loadExecutors) !== JSON.stringify(oldData.loadExecutors)){
//         return updateTruckRateDistribution(newData, truckId, trucksSetDataMethodsObject, true, true);
//     }
// }

// src/**/main_board_components_old/*, src/**/pages/invoices OLD/Invoices.js